import React, { useState } from "react";
import Slider from "@mui/material/Slider";

import HorizontalMenu from "./HorizontalMenu";
import CardPalettePicker from "./forge/components/CardPalettePicker";
import { useCardPalette } from "./forge/components/cardPalette";
import { APPEARANCE, forgetAllAppearance, forgetAppearance, useAppearance } from "./appearance";
import { cardLook, cardWeight } from "./forge/components/useForgeCardScene";

/**
 * What the product looks like on this screen, with the thing it sets there to
 * answer.
 * ---------------------------------------------------------------------------
 * The three sizes are three measures of ONE thing - the band across the top of
 * every page - so there is one of it, at the top, and the sliders under it all
 * move the same picture. Three previews of three parts of the same band was
 * three answers to a question nobody asked twice.
 *
 * It is the real band: the real tab strip, the real icon buttons, under the
 * same rules the pages use, so what is drawn here is what will be drawn there.
 * And the choice is written as it is made, so the band behind this window -
 * and this window's own tabs - move with it.
 * ---------------------------------------------------------------------------
 */

const PREVIEW_TABS = ["Applications", "Traceability", "Cloud suitability"];
const PREVIEW_VALUES = ["apps", "traceability", "assess"];

const ICONS = [
  ["Refresh", <path key="r" d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />],
  ["Export", <path key="e" d="M12 3v12M7 10l5 5 5-5M5 20h14" />],
  ["More", <path key="m" d="M12 5.5h.01M12 12h.01M12 18.5h.01" strokeWidth="2.6" />],
];

/* How a setting reads. Most of these are scales - a share of the size the
   thing was drawn at - and read as percentages. One is a magnification, and
   "1200%" is not how anybody says twelve times. */
const say = (which, v) => {
  const { as } = APPEARANCE[which];
  if (as === "x") return `${v}\u00d7`;
  if (as === "px") return `${Math.round(v)}px`;
  return `${Math.round(v * 100)}%`;
};

/* The marks each slider carries: its ends, its default, and the size the band
   was drawn at when that is not the default. */
const marksFor = (which) => {
  const { min, max, def, as } = APPEARANCE[which];
  const at = [min, def, as === "x" || as === "px" ? def : 1, max]
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort((a, b) => a - b);
  return at.map(v => ({ value: v, label: say(which, v) }));
};

function SizeRow({ which, hint, ink, dim, hair }) {
  const [value, setValue] = useAppearance(which);
  const { def, min, max, step, label } = APPEARANCE[which];

  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: ink }}>{label}</span>
        <span style={{
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 11.5, color: dim, fontVariantNumeric: "tabular-nums",
        }}>
          {say(which, value)}{value === def ? " · default" : ""}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: dim }}>{hint}</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <Slider
          value={value}
          min={min}
          max={max}
          step={step}
          marks={marksFor(which)}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => say(which, v)}
          aria-label={label}
          onChange={(e, v) => setValue(v)}
          sx={{
            maxWidth: 440,
            color: "rgb(246,64,13)",
            "& .MuiSlider-markLabel": { fontSize: 10.5, color: dim },
            "& .MuiSlider-rail": { opacity: 0.25 },
          }}
        />
        <button
          type="button"
          onClick={() => forgetAppearance(which)}
          disabled={value === def}
          style={{
            height: 28, padding: "0 12px", borderRadius: 8,
            border: `1px solid ${hair}`, background: "transparent",
            color: value === def ? dim : ink,
            fontFamily: "inherit", fontSize: 12,
            cursor: value === def ? "default" : "pointer",
          }}
        >
          Reset
        </button>
      </div>
    </section>
  );
}

/**
 * A setting that is on or off rather than a measure of something.
 *
 * Kept in the same shape as the rows above it - a name, what it is for, a way
 * back to the default - so the panel reads as one list and not as a list with
 * a stray control in it.
 */
function SwitchRow({ which, hint, ink, dim, hair }) {
  const [value, setValue] = useAppearance(which);
  const { def, label } = APPEARANCE[which];
  const on = value >= 1;

  return (
    <section style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={label}
          onClick={() => setValue(on ? 0 : 1)}
          style={{
            width: 38, height: 22, flex: "0 0 auto", padding: 2,
            borderRadius: 999, cursor: "pointer",
            border: `1px solid ${on ? "rgb(246,64,13)" : hair}`,
            background: on ? "rgba(246,64,13,0.22)" : "transparent",
            display: "flex", justifyContent: on ? "flex-end" : "flex-start",
            transition: "background 140ms cubic-bezier(0.2,0,0,1), border-color 140ms",
          }}
        >
          <span style={{
            width: 16, height: 16, borderRadius: "50%",
            background: on ? "rgb(246,64,13)" : dim,
            transition: "background 140ms",
          }} />
        </button>

        <span style={{ fontSize: 13.5, fontWeight: 600, color: ink }}>{label}</span>
        {value !== def && (
          <span style={{
            fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11.5, color: dim,
          }}>
            on
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: dim, textAlign: "right", maxWidth: 420 }}>
          {hint}
        </span>
      </div>
    </section>
  );
}

export default function AppearanceSettings({ isDarkMode, compact = false }) {
  const [at, setAt] = useState("traceability");
  const [menu] = useAppearance("menu");
  const [height] = useAppearance("height");

  /* The palette here is the one everything shares: a setting in a panel called
     Appearance is not "this page only", so choosing one puts it everywhere -
     which is the action the picker calls `applyEverywhere`. */
  const { shared, colors: swatches, applyEverywhere } = useCardPalette("apps");
  /* read so the swatches below redraw as the range and the switch move */
  const [from] = useAppearance("alphaFrom");
  const [to] = useAppearance("alphaTo");
  const [solid] = useAppearance("solid");

  const ink = isDarkMode ? "rgba(245,239,230,0.9)" : "rgba(17,17,17,0.9)";
  const dim = isDarkMode ? "rgba(245,239,230,0.5)" : "rgba(17,17,17,0.55)";
  const hair = isDarkMode ? "rgba(245,239,230,0.12)" : "rgba(17,17,17,0.12)";

  const bandH = Math.round(78 * height);

  return (
    <div style={{
      maxWidth: compact ? 640 : 760,
      color: ink,
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    }}>
      {/* ── The band, once, at the top ──────────────────────────────────
          Everything below it is a measure of this. `.page-header` is on it so
          the icon buttons are sized by the rule that sizes the real ones. */}
      <span style={{
        display: "block", marginBottom: 8,
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase", color: dim,
      }}>
        Page header
      </span>

      <div
        className="page-header"
        style={{
          boxSizing: "border-box",
          width: "100%",
          height: bandH,
          marginBottom: 24,
          padding: "0 16px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          border: `1px solid ${hair}`,
          borderRadius: 12,
          background: isDarkMode ? "rgba(245,239,230,0.04)" : "rgba(17,17,17,0.03)",
          overflow: "hidden",
          transition: "height 160ms cubic-bezier(0.2, 0, 0, 1)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, whiteSpace: "nowrap" }}>Discovered servers</span>
          <span style={{
            fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
            fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: dim, whiteSpace: "nowrap",
          }}>
            128 systems
          </span>
        </div>

        {/* the header's own squares, at the size the setting asks for */}
        <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
          {ICONS.map(([name, path]) => (
            <div key={name} className="refreshButton" title={name} style={{ pointerEvents: "none" }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {path}
              </svg>
            </div>
          ))}
        </div>

        {/* ...and the strip, which places itself */}
        <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0, height: Math.round(34 * menu) }}>
          <HorizontalMenu
            isDark={isDarkMode}
            addLabel={false}
            noScale={true}
            alignleft={true}
            scale={menu}
            value={at}
            setValue={setAt}
            menuValues={PREVIEW_VALUES}
            menuItems={PREVIEW_TABS}
            onChangeValue={(e, v) => { if (v) setAt(v); }}
          />
        </div>
      </div>

      {/* ── and the three measures of it ───────────────────────────────── */}
      <SizeRow which="menu" hint="the tabs" ink={ink} dim={dim} hair={hair} />
      <SizeRow which="icon" hint="the square buttons" ink={ink} dim={dim} hair={hair} />
      <SizeRow which="height" hint="the band itself" ink={ink} dim={dim} hair={hair} />

      {/* ── the cards ──────────────────────────────────────────────────── */}
      <section style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${hair}` }}>
        {/* The range, as its two ends. Every card takes a place between them
            from its own id, so these two together are the spread as well as
            the weight. */}
        <SizeRow
          which="alphaFrom"
          hint="the palest a card may be"
          ink={ink}
          dim={dim}
          hair={hair}
        />
        <SizeRow
          which="alphaTo"
          hint="and the most solid"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        {/* ...and whether that weight is spent on transparency at all */}
        <SwitchRow
          which="solid"
          hint="spend the weight on the colour instead — the same shade, mixed with the page, painted opaque, so nothing shows through a card"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        {/* ...or not a painted surface at all */}
        <SwitchRow
          which="glass"
          hint="make them panes rather than surfaces — the ground shows through, the edge is what is lit, and the panel beside the stage goes see-through with them. Takes the place of solid cards"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        {/* how thick they are, and whether anything is lighting them */}
        <SizeRow
          which="depth"
          hint="how thick a card is — GPU renderer only"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        <SwitchRow
          which="lights"
          hint="put lights in the scene and a bloom on the frame — slower, and a great deal more to look at"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        {/* how far the wheel goes on a shelf of them */}
        <SizeRow
          which="zoom"
          hint="how far the wheel goes into a shelf of cards"
          ink={ink}
          dim={dim}
          hair={hair}
        />

        {/* eight of them at the weight being chosen, since one card cannot
            show the thing this setting is for: the spread between them */}
        <div
          /* the stage's own tint variables, without the stage: a swatch that
             is not painted from the same tokens the cards are is a swatch
             showing somebody else's cards */
          className="forge-page forge-wiki-tints"
          /* .forge-page is worn for its TOKENS - the surface the solid cards
             are mixed with - and it brings a column layout and a full height
             with it, so both are said again here. */
          style={{
            display: "flex",
            flexDirection: "row",
            height: 34,
            gap: 6,
            marginTop: -8,
            background: "transparent",
          }}
        >
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
            const look = cardLook(`sample-${i}`, swatches, { from, to, solid: solid >= 1 });
            return (
              <span
                key={i}
                title={`${Math.round(cardWeight(`sample-${i}`, { from, to }) * 100)}%`}
                style={{
                  flex: "1 1 0",
                  height: 34,
                  borderRadius: 7,
                  border: `1px solid ${hair}`,
                  background: look["--forge-wiki-card-tint"],
                  opacity: look["--forge-wiki-card-opacity"],
                }}
              />
            );
          })}
        </div>
      </section>

      {/* ── the colour the cards and the boards are painted from ───────── */}
      <section style={{ marginTop: 22, marginBottom: 10 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>Colour palette</span>
        <p style={{ margin: "4px 0 12px", fontSize: 12.5, lineHeight: 1.5, color: dim }}>
          What the cards and the dashboards are painted from. Chosen here it goes
          everywhere — the wiki, the estate, the applications and every board.
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 10,
          padding: "7px 10px", border: `1px solid ${hair}`, borderRadius: 10,
        }}>
          <CardPalettePicker
            value={shared}
            onChange={(next) => applyEverywhere(next)}
            onApplyAll={(next) => applyEverywhere(next)}
            scopeLabel="everywhere"
          />
          <span style={{ fontSize: 12.5, color: dim }}>Pick a palette, or mix your own</span>
        </div>
      </section>

      <button
        type="button"
        onClick={() => forgetAllAppearance()}
        style={{
          marginTop: 6, height: 30, padding: "0 14px", borderRadius: 8,
          border: `1px solid ${hair}`, background: "transparent", color: dim,
          fontFamily: "inherit", fontSize: 12, cursor: "pointer",
        }}
      >
        Reset to default
      </button>
    </div>
  );
}
